/**
 * SystemLearnsSwimlane (Plan v9 §4.2 prototype rework).
 *
 * Renders the canonical "How the System Learns" 6-step swimlane from
 * the prototype at
 * https://ai-native-prototype.vercel.app/care-pathway-studio.html .
 * Each step carries an icon, a title, and a one-sentence caption. On
 * wide viewports the steps lay out horizontally with subtle connector
 * lines between them; on narrow viewports they stack vertically with
 * the connectors rendered as a left rail (mobile-friendly per
 * Plan v9 §4.2 deliverable 1).
 *
 * Data is hardcoded - the swimlane is descriptive content, not
 * telemetry. Keeping it inline here avoids an extra fixture file for
 * what is effectively a piece of marketing copy that mirrors the
 * prototype.
 *
 * HC2 token-only: every color, spacing, radius value resolves through
 * Signal DS tokens via Tailwind. No raw hex.
 * HC5: zero em / en dashes anywhere. Hyphens only.
 * HC6 a11y: the swimlane is `role="list"`, each step is `role="listitem"`.
 *   Step icon glyphs are `aria-hidden="true"` so the SR announcement is
 *   the title + caption only.
 */
import type { ReactNode } from 'react';

/** One step in the 6-step swimlane. */
export interface SwimlaneStep {
  /** Stable id for React keys + data-testid. */
  id: string;
  /** Display glyph (emoji). Aria-hidden so SR users hear title + caption only. */
  icon: string;
  /** One-line step title. */
  title: string;
  /** One-sentence caption explaining the step. */
  caption: string;
}

/**
 * Canonical 6-step list, mirroring the prototype's swimlane copy
 * verbatim where possible. The 6th step intentionally has no glyph
 * because the prototype renders it as an "outcome" callout rather
 * than a numbered step.
 */
export const SYSTEM_LEARNS_STEPS: ReadonlyArray<SwimlaneStep> = [
  {
    id: 'step-1-encounter',
    icon: 'STETH',
    title: 'Encounter Happens',
    caption:
      'Patient visit completed. SOAP note drafted by ambient AI from ClinicalDesk.',
  },
  {
    id: 'step-2-suggestion',
    icon: 'AI',
    title: 'AI Generates Suggestion',
    caption:
      'NBA card fires. RAG query pulls from scrips-cc-index for similar past encounters.',
  },
  {
    id: 'step-3-decision',
    icon: 'DEC',
    title: 'Clinician Approves or Overrides',
    caption:
      'Each tap is a signal. Approve to reinforce. Override + free-text reason to add a correction.',
  },
  {
    id: 'step-4-index',
    icon: 'IDX',
    title: 'Event Indexed in Azure',
    caption:
      'Provenance to embedding pipeline to search index. Override reason phrase added as context vector.',
  },
  {
    id: 'step-5-better',
    icon: 'NXT',
    title: 'Next Suggestion Is Better',
    caption: "RAG now includes this clinic's real override patterns.",
  },
  {
    id: 'step-6-no-retraining',
    icon: 'OK',
    title: 'No Model Retraining Required',
    caption:
      'System improves via RAG retrieval context per clinic and specialty, not model retraining.',
  },
];

/**
 * Render the swimlane.
 *
 * Props are intentionally minimal so the component can be reused if
 * the copy is ever lifted into a fixture file; for Phase 1 the
 * canonical list is the default.
 */
export interface SystemLearnsSwimlaneProps {
  /** Override list of steps. Defaults to the canonical 6 steps. */
  steps?: ReadonlyArray<SwimlaneStep>;
  /** Optional className for the outer wrapper. */
  className?: string;
}

export function SystemLearnsSwimlane({
  steps = SYSTEM_LEARNS_STEPS,
  className,
}: SystemLearnsSwimlaneProps): ReactNode {
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-labelledby="system-learns-swimlane-title"
      data-testid="system-learns-swimlane"
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="system-learns-swimlane-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          How the System Learns
        </h3>
        <p className="text-signal-sm text-text-secondary">
          Every clinician action becomes training signal. Six-step loop:
          encounter, suggestion, decision, index, improved suggestion, no
          model retraining required.
        </p>
      </header>
      <ol
        role="list"
        aria-label="Six-step learning loop"
        data-testid="system-learns-swimlane-list"
        className="flex flex-col gap-gutter-md md:flex-row md:items-stretch md:gap-gutter-sm"
      >
        {steps.map((step, idx) => (
          <li
            key={step.id}
            role="listitem"
            data-testid={`swimlane-${step.id}`}
            data-step-index={idx + 1}
            className="relative flex flex-1 flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-muted p-gutter-md"
          >
            <div className="flex items-center gap-gutter-sm">
              <span
                aria-hidden="true"
                data-testid={`swimlane-${step.id}-icon`}
                className="inline-flex h-8 min-w-8 items-center justify-center rounded-signal-pill bg-signal-primary-100 px-2 font-mono text-signal-xs font-semibold text-signal-primary-700"
              >
                {step.icon}
              </span>
              <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
                Step {idx + 1}
              </span>
            </div>
            <p className="text-signal-base font-semibold text-text-primary">
              {step.title}
            </p>
            <p className="text-signal-sm text-text-secondary">
              {step.caption}
            </p>
            {idx < steps.length - 1 ? (
              <span
                aria-hidden="true"
                data-testid={`swimlane-${step.id}-connector`}
                className="hidden md:block md:absolute md:right-[-0.625rem] md:top-1/2 md:h-[2px] md:w-gutter-md md:-translate-y-1/2 md:bg-surface-border"
              />
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
